import { max } from "lodash-es"

import { MONITORING_HISTORY_START } from "@/constants/terminal/monitoring-history"
import { formatTerminalDate } from "@/lib/terminal/date"
import type {
  TerminalDetectionView,
  TerminalScoreChange,
  TerminalScoreMovement,
} from "@/lib/terminal/file-view"

import { WhatHappened, type WhatHappenedLine } from "./what-happened"

// The absence line for a monitored Company or Portfolio without a counted
// score change.
export const NO_ACTIVITY = `No reassessments since ${formatTerminalDate(MONITORING_HISTORY_START)}`

// When a counted score change happened: the newest event day among the
// Detections it added, since the check that found them can come weeks after
// the event; otherwise the day the Assessment ran. An aging Detection keeps
// the Assessment's day, as its event can be years old.
export const changeDate = (
  {
    assessedAt,
    movement,
  }: Pick<TerminalScoreChange, "assessedAt" | "movement">,
  detections: TerminalDetectionView[]
) =>
  max(
    movement.drivers.flatMap(({ detectionChange, detectionId }) =>
      detectionChange === "new"
        ? detections.flatMap(({ id, eventDate }) =>
            id === detectionId && eventDate ? [eventDate] : []
          )
        : []
    )
  ) ?? assessedAt

// What happened in one counted score change: the Score Inputs that moved it,
// as arrowed lines (see what-happened.tsx). A Detection's line opens the
// Company's own Detection, found by its durable id in `detections`; a
// Detection that left the board, like any other Score Input, is a sentence.
export const ChangeDescription = ({
  movement,
  detections,
  className,
}: {
  movement: TerminalScoreMovement
  detections: TerminalDetectionView[]
  className?: string
}) => {
  const lines = movement.drivers.map(
    ({ direction, detectionId, detectionChange, text }): WhatHappenedLine => {
      const detection = detections.find(({ id }) => id === detectionId)
      return detection
        ? {
            kind: "detection",
            direction,
            detection,
            change: detectionChange,
          }
        : { kind: "sentence", direction, text }
    }
  )
  return <WhatHappened lines={lines} className={className} />
}
